// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Stage 2 of the Power Flow model: electrical depth.
 *
 * See docs/power-flow/power-flow-single-line-diagram.md §7.2.
 *
 * `rank(v) = 0` for a vertex with no upstream, otherwise
 * `1 + max(rank(u))` over all upstream `u` — **longest** path, deliberately
 * not shortest. A PDU that is fed both by a UPS and directly from the input bus
 * has a shortest path of 1 and a longest of 2; shortest-path layering would
 * float it up level with the UPS that feeds it, which is precisely the drawing
 * error a one-line must never make. Longest-path guarantees every conductor
 * points downward.
 *
 * **Cycles are expected, not exceptional.** An A/B bus tie makes the graph
 * cyclic by construction (primer invariant 5), so the pass finds strongly
 * connected components, drops the edges that close them by a rule that does not
 * depend on input order, and *records* what it dropped. A tie is exactly the
 * edge an operator wants to see, so it must reach the renderer rather than
 * vanish.
 */

import type { PowerFlowGraph, PowerFlowLink, PowerFlowVertex } from './powerFlowGraph';

export interface PowerFlowRanking {
  /** Vertex id → electrical depth, 0 at the sources. */
  rankOf: Map<string, number>;
  /** Vertex ids per rank, unordered here; `powerFlowLayout` orders them. */
  ranks: string[][];
  /** Links that survive as downward conductors, sorted by id. */
  treeLinks: PowerFlowLink[];
  /**
   * Links removed to break a cycle, sorted by id. Not discarded — the renderer
   * draws them distinctly, because a bus tie is information.
   */
  cycleLinks: PowerFlowLink[];
  /**
   * Rank-0 vertices whose ontology role is not `source` or `bus`.
   *
   * A `PDUType` at rank 0 does not mean the site has a PDU wired to the
   * utility; it means an upstream `feeds` is missing from the payload. Reported
   * rather than special-cased, per design §7.1.
   */
  suspectRoots: string[];
  /**
   * Vertices whose role is `load` but which feed something anyway, or `source`
   * but which are fed by something. Contradicts the SHACL profile, so it is a
   * data problem worth surfacing — never a layout exception.
   */
  roleViolations: string[];
}

/**
 * Rank the graph.
 *
 * Deterministic in every respect: SCC discovery walks vertices and neighbours
 * in sorted order, the cycle-break rule is a total order, and the layering is a
 * fixpoint. Shuffling the inputs is asserted to produce an identical ranking.
 */
export const rankPowerFlow = (graph: PowerFlowGraph): PowerFlowRanking => {
  const { vertices, links } = graph;

  const cycleLinkIds = findCycleBreakingLinks(vertices, links);
  const treeLinks = links.filter(link => !cycleLinkIds.has(link.id));
  const cycleLinks = links.filter(link => cycleLinkIds.has(link.id));

  // Upstream adjacency restricted to the acyclic remainder.
  const upstream = new Map<string, string[]>();
  const downstream = new Map<string, string[]>();
  for (const vertex of vertices) {
    upstream.set(vertex.id, []);
    downstream.set(vertex.id, []);
  }
  for (const link of treeLinks) {
    upstream.get(link.target)?.push(link.source);
    downstream.get(link.source)?.push(link.target);
  }

  const rankOf = longestPathRanks(vertices, upstream, downstream);

  const maxRank = Array.from(rankOf.values()).reduce((max, rank) => Math.max(max, rank), 0);
  const ranks: string[][] = Array.from({ length: vertices.length ? maxRank + 1 : 0 }, () => []);
  for (const vertex of vertices) {
    ranks[rankOf.get(vertex.id) ?? 0].push(vertex.id);
  }

  const suspectRoots = vertices
    .filter(
      vertex => rankOf.get(vertex.id) === 0 && vertex.role !== 'source' && vertex.role !== 'bus'
    )
    .map(vertex => vertex.id);

  const roleViolations = vertices
    .filter(vertex => {
      if (vertex.role === 'load' && vertex.out.length > 0) return true;
      if (vertex.role === 'source' && vertex.in.length > 0) return true;
      return false;
    })
    .map(vertex => vertex.id);

  return { rankOf, ranks, treeLinks, cycleLinks, suspectRoots, roleViolations };
};

/**
 * Longest-path layering, as a fixpoint rather than a topological sweep.
 *
 * Kahn's algorithm would be the textbook choice, but it needs a topological
 * order, and one only exists if the cycle break above was complete. Relaxing to
 * a fixpoint instead means a graph that is still cyclic — a break rule bug, or
 * a shape we have not anticipated — terminates with a usable layering instead
 * of hanging or silently dropping vertices. The iteration cap is the guarantee:
 * a correct DAG converges in at most (longest path + 1) passes, which is
 * bounded by the vertex count.
 */
const longestPathRanks = (
  vertices: readonly PowerFlowVertex[],
  upstream: ReadonlyMap<string, string[]>,
  downstream: ReadonlyMap<string, string[]>
): Map<string, number> => {
  const rankOf = new Map<string, number>();
  for (const vertex of vertices) rankOf.set(vertex.id, 0);

  const maxPasses = vertices.length + 1;
  for (let pass = 0; pass < maxPasses; pass += 1) {
    let changed = false;
    for (const vertex of vertices) {
      const parents = upstream.get(vertex.id) ?? [];
      if (parents.length === 0) continue;
      const deepest = parents.reduce((max, parent) => Math.max(max, rankOf.get(parent) ?? 0), -1);
      const candidate = deepest + 1;
      if (candidate > (rankOf.get(vertex.id) ?? 0)) {
        rankOf.set(vertex.id, candidate);
        changed = true;
      }
    }
    if (!changed) break;
  }

  // A bus with children but no parent belongs at the depth its siblings sit at,
  // not at 0 — otherwise a switchboard fed only through a tie edge that was
  // just removed jumps to the top of the drawing. Pull such a vertex down to
  // one above its shallowest child, but never above a real parent's rank.
  for (const vertex of vertices) {
    if ((upstream.get(vertex.id) ?? []).length > 0) continue;
    const children = downstream.get(vertex.id) ?? [];
    if (children.length === 0) continue;
    const shallowestChild = children.reduce(
      (min, child) => Math.min(min, rankOf.get(child) ?? 0),
      Number.POSITIVE_INFINITY
    );
    if (Number.isFinite(shallowestChild) && shallowestChild - 1 > 0) {
      rankOf.set(vertex.id, shallowestChild - 1);
    }
  }

  return rankOf;
};

/**
 * Which links to remove so the graph becomes acyclic.
 *
 * Two rules, and the first is the one that matters electrically.
 *
 * **An antiparallel pair is a tie, in both directions.** When the payload says
 * A feeds B *and* B feeds A, that is what a closed tie breaker between two
 * switchboards looks like — neither direction is a hierarchical feed, and the
 * two boards sit at the same depth. Removing only one of them would leave the
 * survivor acting as a genuine conductor and rank one board below the other,
 * which is wrong on the drawing and wrong in the answer to "if this fails, what
 * goes dark?". So both go, and both are recorded as ties.
 *
 * **A longer ring keeps one edge's worth of hierarchy.** For a cycle with no
 * antiparallel pair (three boards in a ring), one edge closes it and the rest
 * are real feeds. Which one is chosen by a total order:
 *
 *   1. the link whose source has the most upstream neighbours — that vertex is
 *      the more deeply fed of the pair, so the edge leaving it is the one
 *      running back *up* the drawing;
 *   2. then the link whose target has the fewest;
 *   3. then lexicographically by link id.
 *
 * Rule 3 alone would be enough for determinism. Rules 1 and 2 are what make the
 * choice electrically sensible.
 */
const findCycleBreakingLinks = (
  vertices: readonly PowerFlowVertex[],
  links: readonly PowerFlowLink[]
): Set<string> => {
  const removed = new Set<string>();
  const inDegree = new Map<string, number>();
  for (const vertex of vertices) inDegree.set(vertex.id, vertex.in.length);

  // Antiparallel pairs first, both halves.
  const linkIds = new Set(links.map(link => link.id));
  for (const link of links) {
    if (linkIds.has(`${link.target}->${link.source}`)) {
      removed.add(link.id);
    }
  }

  // Re-run after each removal: breaking one cycle can leave a smaller SCC
  // behind (two ties between the same pair of boards, a three-board ring), and
  // one pass would leave the graph cyclic. Bounded by the link count, so it
  // terminates.
  for (let guard = 0; guard <= links.length; guard += 1) {
    const live = links.filter(link => !removed.has(link.id));
    const components = stronglyConnectedComponents(vertices, live);
    const cyclic = components.find(component => component.length > 1);
    if (!cyclic) return removed;

    const members = new Set(cyclic);
    const candidates = live.filter(link => members.has(link.source) && members.has(link.target));
    if (candidates.length === 0) return removed;

    candidates.sort((a, b) => {
      const bySource = (inDegree.get(b.source) ?? 0) - (inDegree.get(a.source) ?? 0);
      if (bySource !== 0) return bySource;
      const byTarget = (inDegree.get(a.target) ?? 0) - (inDegree.get(b.target) ?? 0);
      if (byTarget !== 0) return byTarget;
      return a.id < b.id ? -1 : 1;
    });
    removed.add(candidates[0].id);
  }

  return removed;
};

/**
 * Tarjan's strongly-connected components, iterative.
 *
 * Iterative rather than recursive because the recursion depth is the length of
 * the longest chain in the graph, and a site's power chain is shallow but its
 * *entity* count is not — a payload where telemetry filtering fails would blow
 * the stack rather than report a problem.
 *
 * Vertices and their neighbours are visited in sorted order so the components
 * come out in a stable order regardless of pagination.
 */
export const stronglyConnectedComponents = (
  vertices: readonly PowerFlowVertex[],
  links: readonly PowerFlowLink[]
): string[][] => {
  const successors = new Map<string, string[]>();
  for (const vertex of vertices) successors.set(vertex.id, []);
  for (const link of links) successors.get(link.source)?.push(link.target);
  for (const list of Array.from(successors.values())) list.sort();

  const index = new Map<string, number>();
  const lowLink = new Map<string, number>();
  const onStack = new Set<string>();
  const stack: string[] = [];
  const components: string[][] = [];
  let counter = 0;

  const ids = vertices
    .map(vertex => vertex.id)
    .slice()
    .sort();

  for (const root of ids) {
    if (index.has(root)) continue;

    // Each frame carries how far through its successor list it has walked.
    const frames: { id: string; next: number }[] = [{ id: root, next: 0 }];
    index.set(root, counter);
    lowLink.set(root, counter);
    counter += 1;
    stack.push(root);
    onStack.add(root);

    while (frames.length > 0) {
      const frame = frames[frames.length - 1];
      const neighbours = successors.get(frame.id) ?? [];

      if (frame.next < neighbours.length) {
        const next = neighbours[frame.next];
        frame.next += 1;
        if (!index.has(next)) {
          index.set(next, counter);
          lowLink.set(next, counter);
          counter += 1;
          stack.push(next);
          onStack.add(next);
          frames.push({ id: next, next: 0 });
        } else if (onStack.has(next)) {
          lowLink.set(frame.id, Math.min(lowLink.get(frame.id)!, index.get(next)!));
        }
        continue;
      }

      frames.pop();
      const parent = frames[frames.length - 1];
      if (parent) {
        lowLink.set(parent.id, Math.min(lowLink.get(parent.id)!, lowLink.get(frame.id)!));
      }

      if (lowLink.get(frame.id) === index.get(frame.id)) {
        const component: string[] = [];
        for (;;) {
          const member = stack.pop() as string;
          onStack.delete(member);
          component.push(member);
          if (member === frame.id) break;
        }
        components.push(component.sort());
      }
    }
  }

  return components;
};
